import { getReturnOrderChatUrl } from '@apis/seller-center/chat';
import { orderActionApi, returnOrdersListingApi, updateStatus } from '@apis/seller-center/orders.js';
import {
  Actions,
  Badge,
  Button,
  Group,
  LinkWithPermission,
  Modal,
  Skeleton,
  Status,
  Table,
  Tabs,
  TextCopy,
  TextWithIcon,
  Toast,
} from '@common-web/index.js';
import {
  convertQueryObjToString,
  handleFilterChips,
  mapQueryStringToFilterObject,
} from '@components-web/filters/helper';
import { Filters } from '@components-web/index.js';
import { PERMISSION_MODELS, pageSizeOptions } from '@constants/generic-constants.js';
import {
  ACTION_INDEX,
  ACTION_SHOW,
  PERMISSION_KEY_ORDER,
  PERMISSION_KEY_PRODUCT,
} from '@constants/permission-constants.js';
import { CHAT_PATH, SC_RETURN_ORDERS } from '@constants/seller-center-paths.js';
import { strings } from '@constants/strings.js';
import {
  changeTabFromSearchQuery,
  formatCurrency,
  formatPhoneNo,
  getAggregationsCount,
  getErrorString,
  getFormattedDate,
  getQueryParams,
  onTabClickChange,
} from '@utils/utils.js';
import { Divider, Flex, Image, Space } from 'antd';
import PermissionsHelper from 'auth/helper/PermissionHelper';
import ReturnOrderDetailModal from 'pages/cp/orders/return-order/return-order-detail-modal';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BsEye } from 'react-icons/bs';
import { FaCheckCircle } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import placeholderImage from '../../../../assets/placeholder.png';
import { filterActionsBasedOnPermissions } from '../helper/index.js';
import BulkActionsDock from '../manage-orders/bulk-actions-dock';
import CancelModalBody from '../order-details/components/cancel-modal-body';
import { ORDER_ACTIONS_BUTTON_CONFIG } from '../orders-constants';
import styles from './index.module.scss';
import { RETURN_ORDERS_COLUMNS, RETURN_ORDERS_FILTERS_LIST } from './return-orders-constants';

const ScReturnOrders = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const nav = useNavigate();
  const dispositionModalRef = useRef(null);

  const [filterChips, setFilterChips] = useState([]);
  const [returnOrdersData, setReturnOrdersData] = useState({});
  const [tableDataLoading, setTableDataLoading] = useState(false);
  const [showReturnOrderDetailModal, setShowReturnOrderDetailModal] = useState(false);
  const [tabIndex, setTabIndex] = useState(1);
  const [dispositions, setDispositions] = useState([]);
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkSelectedLineItems, setBulkSelectedLineItems] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dispositionModal, setDispositionModal] = useState({ visible: false, modalData: {} });
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [chatLoading, setChatLoading] = useState(null);

  const { currency } = useSelector((state) => state.tenantData?.tenant ?? {});
  const {
    returns = [],
    pagination,
    aggregations = {},
    statuses_and_dispositions = [],
    bulkActions = {},
  } = returnOrdersData ?? {};

  const filtersList = RETURN_ORDERS_FILTERS_LIST({ dispositions });

  const formattedStatuses = statuses_and_dispositions?.map((status) => ({
    ...status,
    dispositions: status?.dispositions?.filter(Boolean),
  }));

  useEffect(() => {
    setFilterChips(handleFilterChips({ location, filtersList, currency }));
  }, [location]);

  useEffect(() => {
    changeTabFromSearchQuery({
      location,
      statuses: dispositions,
      setTabIndex,
    });
  }, [location.search, statuses_and_dispositions?.length]);

  const fetchReturnOrdersListing = async (params) => {
    setTableDataLoading(true);
    try {
      const res = await returnOrdersListingApi(params);
      if (res?.success) {
        const allDispositions = res?.statuses_and_dispositions?.flatMap((item) =>
          item?.dispositions?.map((disposition) => ({ ...disposition, type: 'disposition', isArray: true }))
        );
        setDispositions(allDispositions);
        setReturnOrdersData({ ...res, aggregations: res?.aggregations?.dispositions });
      }
    } catch (err) {
      Toast.error(getErrorString(err));
    }
    setTableDataLoading(false);
  };

  const updateSelectedOrders = ({ selectedRows = [] }) => {
    const selectedOrders = returns
      ?.filter((order) => selectedRows?.map((record) => record?.key)?.includes(order?.id))
      ?.map((order) => ({ ...order, key: order?.id }));
    setSelectedOrders(selectedOrders);
    setBulkSelectedLineItems(selectedOrders?.flatMap((order) => order?.line_items?.map((item) => item?.id)));
  };

  const rowSelection = {
    selectedRowKeys: selectedOrders?.map((item) => item.key),
    onSelect: (record) => {
      const currentSelected = [...selectedOrders];
      const index = currentSelected?.findIndex((item) => item.key === record.key);
      if (index !== -1) {
        currentSelected?.splice(index, 1);
        updateSelectedOrders({ selectedRows: currentSelected });
      } else {
        updateSelectedOrders({ selectedRows: [...currentSelected, record] });
      }
    },
    getCheckboxProps: (record) => {
      const orderDisposition = returns?.find((order) => order?.id === record?.key)?.disposition?.slug;
      const selectedDisposition = selectedOrders?.[0]?.disposition?.slug;
      const isSameDisposition = !selectedDisposition || selectedDisposition === orderDisposition;
      const isActionAllowed = bulkActions?.[formattedStatuses[tabIndex - 1]?.slug]?.[orderDisposition];

      return {
        disabled: !(isSameDisposition && isActionAllowed),
      };
    },
    onSelectAll: (selected, selectedRows) => {
      if (selected) {
        updateSelectedOrders({ selectedRows });
      } else {
        setSelectedOrders([]);
        setBulkSelectedLineItems([]);
      }
    },
    hideSelectAll: !selectedOrders?.length,
  };

  const handleAction = async (action, order) => {
    if (action?.required_attributes?.reason?.is_required) {
      setDispositionModal({ visible: true, modalData: { ...action, order_id: order?.id } });
    } else {
      setConfirmationModal({
        title: action?.name,
        body: () => (
          <div>
            <Flex vertical gap={12}>
              <div>Are you sure you want to perform this action?</div>
              <Flex align="center" gap={8}>
                <FaCheckCircle color="var(--ant-color-success" size={14} />
                <span className="fw-600">
                  {' '}
                  {strings.order_id} {order?.order_id}
                </span>
              </Flex>
            </Flex>
          </div>
        ),
        action: () => handleReturnOrderAction(action, order),
      });
    }
  };

  const returnOrdersListingData = () => {
    if (returns?.length) {
      return returns?.map((returnOrder) => {
        const {
          order_id,
          net_amount,
          id,
          return_line_items = [],
          status,
          order_delivered_at,
          shipping_address,
          seller_center_possible_actions = [],
          rejection_reason,
          return_reason,
        } = returnOrder;
        const {
          cover_image,
          name,
          net_price,
          quantity,
          external_url,
          line_item,
          can_initiate_chat,
          chat_url,
          id: line_item_id,
        } = return_line_items?.[0] || {};

        return {
          key: order_id,
          [strings.order_id]: <TextCopy value={order_id} />,
          [strings.item_details]: (
            <Flex align="center" gap={12}>
              <Image
                width={92}
                height={92}
                className="image-border"
                src={cover_image?.sizes?.thumbnail ?? placeholderImage}
                alt="Loading..."
                loading="lazy"
                placeholder={<Skeleton width={92} height={92} borderRadius={4} />}
                wrapperClassName="flexShrink0"
                preview={{ mask: <BsEye /> }}
              />
              <Group gap={'4px'}>
                <Space size={0} split={<Divider type="vertical" />} className={styles.productId}>
                  <Flex gap={3}>
                    {strings.product_id} <span>{line_item?.product_id}</span>
                  </Flex>
                  <Flex gap={3}>
                    {strings.sku} <span>{line_item?.sku}</span>
                  </Flex>
                </Space>
                <LinkWithPermission
                  className={styles.productTitle}
                  to={external_url}
                  target="_blank"
                  authorized={
                    PermissionsHelper.getPermissions(PERMISSION_KEY_PRODUCT, ACTION_INDEX) &&
                    status.slug === 'active' &&
                    !!external_url
                  }
                >
                  <div className={styles.productTitle}>
                    <span>{name ?? '-'}</span>
                  </div>
                </LinkWithPermission>
                <Flex gap={4} align={'center'}>
                  <div className={styles.productPrice}>{formatCurrency({ currency, price: net_price })}</div>
                  <span>x{quantity}</span>
                </Flex>
                <span>{line_item?.category?.name}</span>
              </Group>
            </Flex>
          ),
          [strings.buyer]: (
            <Flex vertical gap={4}>
              <span className="fw-700 ml-20">{shipping_address?.name}</span>
              <TextWithIcon
                iconSize={16}
                iconColor={'var(--gray-300)'}
                icon={'MdPhone'}
                component={
                  <TextCopy
                    value={formatPhoneNo(shipping_address?.mobile)}
                    style={{ fontSize: 'var(--ant-font-size-sm)' }}
                    removeSpaces={true}
                  />
                }
              />
              {shipping_address?.address && (
                <TextWithIcon
                  icon={'BiSolidBuildingHouse'}
                  iconSize={16}
                  iconColor={'var(--gray-300)'}
                  component={
                    <span className={'text-ellipsis'} style={{ maxWidth: 300 }} title={shipping_address?.address}>
                      {shipping_address?.address}
                    </span>
                  }
                />
              )}
            </Flex>
          ),
          [strings.delivered_on]: (
            <Flex align="center" gap={4}>
              <div>{getFormattedDate(order_delivered_at)}</div>
            </Flex>
          ),
          [strings.total_refund]: <span className="fw-700">{formatCurrency({ currency, price: net_amount })}</span>,
          [strings.status]: status ? (
            <Flex gap={8} vertical align="start">
              <Status
                color={status?.background_hex?.seller_center}
                textColor={status?.colour_hex?.seller_center}
                name={status?.name}
              />
            </Flex>
          ) : (
            '-'
          ),
          [strings.rejection_reason]: <span>{rejection_reason ?? '-'}</span>,
          [strings.return_reason]: <span>{return_reason ?? '-'}</span>,
          [strings.actions]: (
            <Actions
              actions={[
                PermissionsHelper.getPermissions(PERMISSION_KEY_ORDER, ACTION_SHOW) && {
                  value: strings.view_detail,
                  onClick: (e) => {
                    e.stopPropagation();
                    setShowReturnOrderDetailModal(id);
                  },
                  show: true,
                  icon: 'IconEyeFilled',
                },
                ...seller_center_possible_actions.map((action) => ({
                  value: action?.name,
                  onClick: () => handleAction(action, returnOrder),
                  show: true,
                  icon: ORDER_ACTIONS_BUTTON_CONFIG?.[action?.slug]?.icon,
                })),
                {
                  value: strings.start_chat,
                  isLink: !!chat_url,
                  show: !!can_initiate_chat,
                  icon: 'IoChatbubbleEllipses',
                  state: {
                    channelUrl: chat_url,
                  },
                  onClick: () => {
                    !chat_url && getChatOrderUrl(line_item_id);
                  },
                  path: CHAT_PATH,
                  loading: line_item?.id === chatLoading && !!chatLoading,
                  disabled: line_item?.id !== chatLoading && !!chatLoading,
                },
              ]}
            />
          ),
        };
      });
    }
  };

  const getChatOrderUrl = async (id) => {
    setChatLoading(id);
    try {
      const response = await getReturnOrderChatUrl(id);
      if (response?.success) {
        nav(CHAT_PATH, {
          state: {
            channelUrl: response?.chat_url,
          },
        });
      }
    } catch (err) {
      Toast.error(getErrorString(err));
    }
    setChatLoading(null);
  };

  const refetchListing = () => {
    setTimeout(() => {
      const params = getQueryParams({ location, pageSize });
      fetchReturnOrdersListing(params);
    }, 500);
  };

  const handleReturnOrderAction = async (action, _returnOrder) => {
    setIsLoading(true);
    try {
      const res = await orderActionApi({
        url: action.url,
        method: action.http_method,
        data: action.payload || {},
      });
      if (res?.success) {
        Toast.success(action?.success_message || 'Action completed successfully');
        refetchListing();
        setConfirmationModal(null);
      }
    } catch (error) {
      Toast.error(getErrorString(error));
    } finally {
      setIsLoading(false);
    }
  };

  const tabsList = [
    {
      key: 0,
      label: (
        <Flex gap={4} justify={'center'}>
          <span>{t('All')}</span>
          <Badge count={getAggregationsCount('all', aggregations)} showZero={true} />
        </Flex>
      ),
      children: (
        <Table
          columns={RETURN_ORDERS_COLUMNS(t, { slug: 'all' })}
          dataSource={returnOrdersListingData()}
          loading={tableDataLoading}
          pagination={pagination}
          setPageSize={setPageSize}
          pageSize={pageSize}
        />
      ),
      show: true,
    },
    ...(dispositions ?? []).map((item, index) => {
      const actions = bulkActions?.[item?.slug] ? Object.values(bulkActions[item.slug]).flat() : [];
      return {
        status: item?.slug,
        key: index + 1,
        label: (
          <Flex gap={4} justify={'center'}>
            <span>{t(item?.name)}</span>
            <Badge count={getAggregationsCount(item?.slug, aggregations)} showZero={true} />
          </Flex>
        ),
        children: (
          <Table
            columns={RETURN_ORDERS_COLUMNS(t, item)}
            dataSource={returnOrdersListingData()}
            loading={tableDataLoading}
            {...(bulkActions?.[item?.slug] && actions?.length
              ? {
                scrollY: 575,
                rowSelection,
                onScrollEnd: async () => {
                  if (pagination?.next_page && !pageLoading) {
                    try {
                      setPageLoading(true);
                      const { queryObj } = mapQueryStringToFilterObject(location?.search);
                      const returnOrdersData = await returnOrdersListingApi(
                        `?${convertQueryObjToString({ ...queryObj, page: pagination?.next_page })}`
                      );
                      setReturnOrdersData((prev) => ({
                        ...returnOrdersData,
                        returns: [...prev.returns, ...returnOrdersData.returns],
                      }));
                    } catch (err) {
                      Toast.error(getErrorString(err));
                    } finally {
                      setPageLoading(false);
                    }
                  }
                },
              }
              : {
                pagination: pagination,
                pageSize,
                setPageSize,
              })}
          />
        ),
        show: true,
      };
    }),
  ];

  const handleDisposition = async () => {
    setIsLoading(true);
    const { selectedReasonId, selectedReasonSlug, customReason } = dispositionModalRef.current.getFormValues();
    try {
      const body = {
        disposition_id: dispositionModal?.modalData?.reason?.reasonable_id,
        reason_id: selectedReasonId,
        ...(selectedReasonSlug === 'other' && { reason_text: customReason }),
      };
      const res = await updateStatus({
        body,
        method: dispositionModal?.modalData?.http_method,
        url: dispositionModal?.modalData?.url,
      });
      if (res.success) {
        Toast.success(`The order has been successfully ${dispositionModal?.modalData?.name?.toLowerCase()}`);
        refetchListing();
        setDispositionModal({ visible: false, modalData: {} });
      }
    } catch (e) {
      Toast.error(getErrorString(e));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex vertical gap={24}>
      <Modal
        open={!!dispositionModal?.visible}
        title={dispositionModal?.modalData?.name}
        ModalBody={
          <CancelModalBody
            data={dispositionModal?.modalData}
            reasonable_id={dispositionModal?.modalData?.required_attributes?.reason?.reasonable_id}
            ref={dispositionModalRef}
            onSubmit={handleDisposition}
          />
        }
        handleCancel={() => {
          setDispositionModal({ visible: false, modalData: {} });
          dispositionModalRef.current.resetForm();
        }}
        handleOk={() => {
          dispositionModalRef?.current?.submitForm();
        }}
        showCustomFooter={true}
        customFooter={[
          <Button
            key="confirm"
            type="primary"
            onClick={() => {
              dispositionModalRef?.current?.submitForm();
            }}
            loading={isLoading}
          >
            Confirm
          </Button>,
        ]}
        closable={true}
        showActions={false}
        destroyOnClose
        width="690px"
        bodyStyle={{ maxHeight: 500 }}
      />

      <Modal
        open={!!confirmationModal}
        title={confirmationModal?.title}
        cancelText={t(strings.cancel)}
        okText={t(strings.confirm)}
        handleOk={() => confirmationModal?.action()}
        width={'572px'}
        handleCancel={() => {
          setConfirmationModal(null);
        }}
        closable={true}
        showActions={true}
        ModalBody={confirmationModal?.body?.()}
        okButtonProps={{ loading: isLoading }}
      />

      <ReturnOrderDetailModal
        open={!!showReturnOrderDetailModal}
        setOpen={setShowReturnOrderDetailModal}
        returnOrderId={showReturnOrderDetailModal}
        setConfirmationModal={setConfirmationModal}
        handleReturnOrderAction={handleReturnOrderAction}
      // onRefund={handleRefund}
      />

      <Filters
        filtersList={filtersList}
        fetch={fetchReturnOrdersListing}
        rootPath={SC_RETURN_ORDERS}
        filterChips={filterChips}
        componentKey={PERMISSION_MODELS.ORDERS}
        loading={tableDataLoading}
        showManageSearchSkeleton={false}
        pageSize={pageSize}
      />

      <Tabs
        tabsList={tabsList}
        loading={tableDataLoading}
        onTabClick={(key) => {
          onTabClickChange({
            key,
            statuses: dispositions,
            nav,
          });
          setSelectedOrders([]);
          setBulkSelectedLineItems([]);
        }}
        activeKey={tabIndex}
      />

      {!!selectedOrders?.length && (
        <BulkActionsDock
          selectedOrders={selectedOrders}
          setSelectedOrders={setSelectedOrders}
          selectedLineItems={bulkSelectedLineItems}
          setSelectedLineItems={setBulkSelectedLineItems}
          applicableOrders={selectedOrders}
          possibleBulkActions={filterActionsBasedOnPermissions(
            bulkActions?.[formattedStatuses[tabIndex - 1]?.slug]?.[selectedOrders[0]?.disposition?.slug],
            PERMISSION_KEY_ORDER
          )}
        />
      )}
    </Flex>
  );
};

export default ScReturnOrders;
